import React, { useState } from 'react';
import ContactService from '../services/ContactService';
import LoadingSpinner from '../utils/LoadingSpinner';
import HeaderTopSection from "../components/partials/HeaderTop";
import Footer from "../components/partials/Footer";
import PhoneNumberInput from "../utils/PhoneNumberInput";
import '../assets/css/ContactPage.css';
import Banner from "../components/partials/Banner";

const ContactPage = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        subject: '',
        message: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [errors, setErrors] = useState({});
    // Incrémenté à chaque reset du formulaire : change la `key` de PhoneNumberInput
    // pour forcer son remontage (et donc la remise à zéro de son état interne)
    // sans avoir à modifier le composant PhoneNumberInput lui-même.
    const [phoneFieldResetKey, setPhoneFieldResetKey] = useState(0);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prevState => ({
                ...prevState,
                [name]: ''
            }));
        }
    };

    // ── Gestion dédiée du numéro de téléphone (composant contrôlé à part) ──
    const handlePhoneChange = (value) => {
        setFormData(prevState => ({
            ...prevState,
            phoneNumber: value
        }));
    };

    const handlePhoneValidation = (errorMsg) => {
        setErrors(prevState => ({
            ...prevState,
            phoneNumber: errorMsg || ''
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'Le prénom est obligatoire';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Le nom est obligatoire';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'L\'email est obligatoire';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'L\'email n\'est pas valide';
        }

        // Le téléphone est facultatif : on ne bloque que s'il est renseigné
        // et jugé invalide par le composant PhoneNumberInput.
        if (errors.phoneNumber) {
            newErrors.phoneNumber = errors.phoneNumber;
        }

        if (!formData.subject.trim()) {
            newErrors.subject = 'Le motif est obligatoire';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Le message est obligatoire';
        } else if (formData.message.length < 10) {
            newErrors.message = 'Le message doit contenir au moins 10 caractères';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setSubmitStatus(null);

        try {
            const response = await ContactService.createContact(formData);

            setSubmitStatus({
                type: 'success',
                message: 'Votre message a été envoyé avec succès ! Vous recevrez une confirmation par email.',
                reference: response.contactReference
            });

            // Reset form
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                subject: '',
                message: ''
            });
            setPhoneFieldResetKey(prev => prev + 1);

        } catch (error) {
            console.error('Erreur lors de l\'envoi du formulaire:', error);
            setSubmitStatus({
                type: 'error',
                message: error.response?.data?.message || 'Une erreur est survenue lors de l\'envoi de votre message. Veuillez réessayer.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            subject: '',
            message: ''
        });
        setErrors({});
        setSubmitStatus(null);
        setPhoneFieldResetKey(prev => prev + 1);
    };

    return (
        <>
            <HeaderTopSection />

            <main className="main overflow-hidden">
                {/* Hero Section */}
                <Banner
                    title="Contactez-nous"
                    subtitle=""
                />

                {/* Contact Form Section */}
                <section className="contact-section py-xxl-25 py-20 bg-light">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-10">
                                <div className="contact-form-container">
                                    {/* Spinner de chargement */}
                                    <LoadingSpinner
                                        isVisible={isLoading}
                                        loadingText="Envoi en cours..."
                                    />

                                    <div className="contact-header">
                                        <h2 className="contact-title">Formulaire de Contact</h2>
                                        <p className="contact-subtitle">
                                            Une question ? Une demande particulière ? Notre équipe est à votre écoute.
                                        </p>
                                    </div>

                                    {/* Message de statut */}
                                    {submitStatus && (
                                        <div className={`alert alert-${submitStatus.type} ${submitStatus.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                                            <div className="alert-content">
                                                <span className="alert-icon">
                                                    {submitStatus.type === 'success' ? '✅' : '❌'}
                                                </span>
                                                <div>
                                                    <p className="alert-message">{submitStatus.message}</p>
                                                    {submitStatus.reference && (
                                                        <p className="alert-reference">
                                                            <strong>Référence :</strong> {submitStatus.reference}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="alert-close"
                                                onClick={() => setSubmitStatus(null)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    )}

                                    <form
                                        onSubmit={handleSubmit}
                                        className="contact-form"
                                        autoComplete="off"
                                    >
                                        {/* ── Prénom / Nom : 2 champs par ligne sur grand écran ── */}
                                        <div className="row g-3">
                                            <div className="col-12 col-sm-6">
                                                <div className="form-group mb-0">
                                                    <label htmlFor="firstName" className="form-label">
                                                        Prénom *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="firstName"
                                                        name="firstName"
                                                        value={formData.firstName}
                                                        onChange={handleChange}
                                                        className={`form-input ${errors.firstName ? 'error' : ''}`}
                                                        placeholder="Votre prénom"
                                                        disabled={isLoading}
                                                        autoComplete="off"
                                                    />
                                                    {errors.firstName && (
                                                        <span className="error-message">{errors.firstName}</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-12 col-sm-6">
                                                <div className="form-group mb-0">
                                                    <label htmlFor="lastName" className="form-label">
                                                        Nom *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="lastName"
                                                        name="lastName"
                                                        value={formData.lastName}
                                                        onChange={handleChange}
                                                        className={`form-input ${errors.lastName ? 'error' : ''}`}
                                                        placeholder="Votre nom"
                                                        disabled={isLoading}
                                                        autoComplete="off"
                                                    />
                                                    {errors.lastName && (
                                                        <span className="error-message">{errors.lastName}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* ── Email / Téléphone : 2 champs par ligne sur grand écran ── */}
                                        <div className="row g-3 mt-1">
                                            <div className="col-12 col-sm-6">
                                                <div className="form-group mb-0">
                                                    <label htmlFor="email" className="form-label">
                                                        Adresse email *
                                                    </label>
                                                    <input
                                                        type="email"
                                                        id="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className={`form-input ${errors.email ? 'error' : ''}`}
                                                        placeholder="votre@email.com"
                                                        disabled={isLoading}
                                                        autoComplete="new-password"
                                                    />
                                                    {errors.email && (
                                                        <span className="error-message">{errors.email}</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-12 col-sm-6">
                                                <div className="form-group mb-0">
                                                    <label htmlFor="phoneNumber" className="form-label">
                                                        Numéro de téléphone
                                                    </label>
                                                    <PhoneNumberInput
                                                        key={phoneFieldResetKey}
                                                        value={formData.phoneNumber}
                                                        onChange={handlePhoneChange}
                                                        onValidation={handlePhoneValidation}
                                                        disabled={isLoading}
                                                        defaultCountry="CM"
                                                    />
                                                    {errors.phoneNumber && (
                                                        <span className="error-message">{errors.phoneNumber}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* ── Motif : pleine largeur ── */}
                                        <div className="row g-3 mt-1">
                                            <div className="col-12">
                                                <div className="form-group mb-0">
                                                    <label htmlFor="subject" className="form-label">
                                                        Motif de contact *
                                                    </label>
                                                    <select
                                                        id="subject"
                                                        name="subject"
                                                        value={formData.subject}
                                                        onChange={handleChange}
                                                        className={`form-select ${errors.subject ? 'error' : ''}`}
                                                        disabled={isLoading}
                                                        autoComplete="off"
                                                    >
                                                        <option value="">Sélectionnez un motif</option>
                                                        <option value="Demande d'information">Demande d'information</option>
                                                        <option value="Demande de devis">Demande de devis</option>
                                                        <option value="Problème technique">Problème technique</option>
                                                        <option value="Réclamation">Réclamation</option>
                                                        <option value="Partenariat">Partenariat</option>
                                                        <option value="Autre">Autre</option>
                                                    </select>
                                                    {errors.subject && (
                                                        <span className="error-message">{errors.subject}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* ── Message : pleine largeur ── */}
                                        <div className="row g-3 mt-1">
                                            <div className="col-12">
                                                <div className="form-group mb-0">
                                                    <label htmlFor="message" className="form-label">
                                                        Votre message *
                                                    </label>
                                                    <textarea
                                                        id="message"
                                                        name="message"
                                                        value={formData.message}
                                                        onChange={handleChange}
                                                        className={`form-textarea ${errors.message ? 'error' : ''}`}
                                                        placeholder="Décrivez votre demande en détail..."
                                                        rows="6"
                                                        disabled={isLoading}
                                                        autoComplete="off"
                                                    />
                                                    {errors.message && (
                                                        <span className="error-message">{errors.message}</span>
                                                    )}
                                                    <div className="character-count">
                                                        {formData.message.length}/2000 caractères
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-actions">
                                            <button
                                                type="button"
                                                onClick={resetForm}
                                                className="btn btn-secondary"
                                                disabled={isLoading}
                                            >
                                                Effacer
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={isLoading}
                                            >
                                                {isLoading ? 'Envoi en cours...' : 'Envoyer le message'}
                                            </button>
                                        </div>

                                        <div className="form-footer">
                                            <p className="form-note">
                                                * Champs obligatoires. Nous nous engageons à vous répondre sous 48 heures.
                                            </p>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Info Section */}
                <section className="contact-info-section py-xxl-20 py-15">
                    <div className="container">
                        <div className="row g-4 justify-content-center">
                            <div className="col-md-4 text-center">
                                <div className="info-card">
                                    <div className="info-icon bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3">
                                        📧
                                    </div>
                                    <h5 className="fw-700 mb-2">Email</h5>
                                    <p className="text-muted mb-0">drivenhomeservices237@gmail.com</p>
                                </div>
                            </div>
                            <div className="col-md-4 text-center">
                                <div className="info-card">
                                    <div className="info-icon bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3">
                                        📞
                                    </div>
                                    <h5 className="fw-700 mb-2">Téléphone</h5>
                                    <p className="text-muted mb-0">237 6 74 69 85 18</p>
                                </div>
                            </div>
                            <div className="col-md-4 text-center">
                                <div className="info-card">
                                    <div className="info-icon bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3">
                                        ⏰
                                    </div>
                                    <h5 className="fw-700 mb-2">Réponse</h5>
                                    <p className="text-muted mb-0">Sous 48 heures maximum</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </>
    );
};

export default ContactPage;